import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './CookiePopup.module.css';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useLang } from 'hooks/useLang';

interface CookiePopupProps { onClose: () => void; }

type SwitchBtnProps = {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  offLabel?: string;
  onLabel?: string;
};

const SwitchBtn: React.FC<SwitchBtnProps> = ({
  checked,
  onChange,
  disabled,
  offLabel = 'OFF',
  onLabel = 'ON',
}) => {
  const className = [
    styles['switch-btn'],
    checked ? styles['switch-on'] : '',
  ].join(' ').trim();

  const toggle = () => !disabled && onChange(!checked);

  return (
    <button
      type="button"
      className={className}
      role="switch"
      aria-checked={checked}
      onClick={toggle}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          toggle();
        }
      }}
    >
      <span className={styles['off-label']}>{offLabel}</span>
      <span className={styles['on-label']}>{onLabel}</span>
      <span className={styles.circle}>
        <span>{checked ? onLabel : offLabel}</span>
      </span>
    </button>
  );
};

const CookiePopup: React.FC<CookiePopupProps> = ({ onClose }) => {
  const [functional, setFunctional]   = useState(false);
  const [performance, setPerformance] = useState(true);
  const [advertising, setAdvertising] = useState(true);

  const dialogRef = useRef<HTMLElement>(null);

    const { t } = useTranslation();
    const { lang } = useLang();

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
        document.body.style.overflow = prevOverflow;
        prev?.focus?.();
    };
  }, []);

  const acceptAll = () => {
    setFunctional(true); setPerformance(true); setAdvertising(true);
    onClose();
  };
  const acceptSelected = () => { onClose(); };
  const declineAll = () => {
    setFunctional(false); setPerformance(false); setAdvertising(false);
    onClose();
  };

  const node = (
    <div className={styles.popupOverlay}>
      <section className={styles.popup} role="dialog" aria-modal="true" aria-label="Cookie settings" tabIndex={-1} ref={dialogRef}>
        <div className={styles['popup-container']}>
          <p className={styles['cookies-intro']}>
            <Trans i18nKey="cookiePopup.intro"
                components={{
            privacyLink: <Link to={`/${lang}/privacy-policy`} />,
            termsLink:  <Link to={`/${lang}/terms-and-conditions`} />,  
            }}
            />
          </p>
          <div className={styles['cookies-info']}>
            <h3>{t('cookiePopup.info.title')}</h3>
            <p>{t('cookiePopup.info.description')}</p>
          </div>

          <div className={styles['cookies-list']}>
            <div className={styles['cookies-item']}>
              <div className={styles['cookies-switcher']}>
                <SwitchBtn checked={functional} onChange={setFunctional} />
              </div>
              <div className={styles['cookies-content']}>
                <h4>{t('cookiePopup.categories.functional.title')}</h4>
                <p>{t('cookiePopup.categories.functional.text')}</p>
              </div>
            </div>

            <div className={styles['cookies-item']}>
              <div className={styles['cookies-switcher']}>
                <SwitchBtn checked={performance} onChange={setPerformance} />
              </div>
              <div className={styles['cookies-content']}>
                <h4>{t('cookiePopup.categories.performance.title')}</h4>
                <p>{t('cookiePopup.categories.performance.text')}</p>
              </div>
            </div>

            <div className={styles['cookies-item']}>
              <div className={styles['cookies-switcher']}>
                <SwitchBtn checked={advertising} onChange={setAdvertising} />
              </div>
              <div className={styles['cookies-content']}>
                <h4>{t('cookiePopup.categories.advertising.title')}</h4>
                <p>{t('cookiePopup.categories.advertising.text')}</p>
              </div>
            </div>
          </div>

          <div className={styles['cookies-button']}>
            <div className={styles.btnAccept}>
                <button className={styles.AcceptAll} onClick={acceptAll}>
                    <span className={styles.whiteToGreen}> {t('cookiePopup.buttons.acceptAll')} </span>
                </button>
                <button className={styles.AcceptSelected} onClick={acceptSelected}>
                    <span className={styles.whiteToGreen}> {t('cookiePopup.buttons.acceptSelected')} </span>
                </button>
            </div>
            <div className={styles.btnDecline}>
                <button className={styles.DeclineAll} onClick={declineAll}> 
                    <span className={styles.whiteToRed}> {t('cookiePopup.buttons.declineAll')} </span>
                </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(node, document.body) : node;
};

export default CookiePopup;